import {Alert, Button, Checkbox, Link, RadioGroup, Select, Textarea, TextField} from 'bonprix-design-system';
import {useEffect, useMemo, useRef, useState, useTransition} from 'react';
import fetchWithCsrf from '../../../../../../../../core/src/main/resources/scripts/helper/fetch-with-csrf.js';
import {WidgetInstance} from 'friendly-challenge';
import ModelContainer from '@core/scripts/components/model-container';
import {subjectConcerns as subjectConcernsDE} from './subject-concerns.de.js';
import {subjectConcerns as subjectConcernsAT} from './subject-concerns.at.js';
import {AdditionalFields} from './AdditionalFields.jsx';
import {insertReactComponentIntoString} from '../../helpers.jsx';
import Url from '@core/scripts/helper/url';
import {useContactForm} from './ContactFormContext.jsx';
import {ConcernInfoText as ConcernInfoTextDE} from './ConcernInfoText.de.jsx';
import {ConcernInfoText as ConcernInfoTextAT} from './ConcernInfoText.at.jsx';

const model = ModelContainer.get('servicePageModel');

export function ContactForm({cdaEntry}) {
    const [, startTransition] = useTransition();
    const {formValues, setFormValues} = useContactForm();
    const [submitButtonIsDisabled, setSubmitButtonIsDisabled] = useState(model.friendlyCaptchaEnabled);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');
    const [formErrors, setFormErrors] = useState({});
    const formRef = useRef();
    const fcContainer = useRef();
    const fcWidget = useRef();

    // Note: The sitekey is the same as for newsletter subscription
    const friendlyCaptchaSitekey =
        model.tenantCountry === 'at'
            ? model.friendlyCaptchaSitekeyNewsletterAt
            : model.friendlyCaptchaSitekeyNewsletter;

    const subjectConcerns = useMemo(() => {
        if (model.tenantCountry === 'de') {
            return subjectConcernsDE;
        } else if (model.tenantCountry === 'at') {
            return subjectConcernsAT;
        } else {
            throw new Error(`Unsupported tenant country: ${model.tenantCountry}`);
        }
    }, [model.tenantCountry]);

    useEffect(() => {
        if (!model.friendlyCaptchaEnabled) {
            return;
        }

        if (!fcWidget.current && fcContainer.current) {
            fcWidget.current = new WidgetInstance(fcContainer.current, {
                doneCallback: (solution) => {
                    setFormValues((prev) => ({...prev, 'frc-captcha-solution': solution}));
                    setSubmitButtonIsDisabled(false);
                },
            });
        }

        return () => {
            if (fcWidget.current != undefined) fcWidget.current.reset();
        };
    }, [fcContainer]);

    const handleChange = (name) => (value) => {
        startTransition(() => setFormValues((prev) => ({...prev, [name]: value})));
        if (name == 'subjectIndex') {
            const subject = subjectConcerns.find((s) => s.value == value)?.subject;
            startTransition(() =>
                setFormValues((prev) => ({
                    ...prev,
                    subject: subject,
                    concern: '',
                    concernIndex: '0',
                })),
            );
        }
        if (name == 'concernIndex') {
            const concern = subjectConcerns
                .find((s) => s.value == formValues.subjectIndex)
                ?.concerns.find((c) => c.value == value)?.concern;
            startTransition(() => setFormValues((prev) => ({...prev, concern: concern})));
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        setFormErrors({});
        setErrorMsg('');
        setIsSubmitting(true);

        fetchWithCsrf(Url.toAbsolute(cdaEntry.fields.subscribeApiUrl), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(formValues),
        })
            .then((response) => {
                response.json().then((data) => {
                    if (data.errors) {
                        setFormErrors(data.errors);
                        //try to scroll to first error
                        formRef.current.querySelector(`[name="${Object.keys(data.errors)[0]}"]`)?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                    }
                    if (data.errorMsg) {
                        setErrorMsg(cdaEntry.fields[data.errorMsg]);
                    }

                    if (data.errors || data.errorMsg) {
                        if (model.friendlyCaptchaEnabled) {
                            fcWidget.current.reset();
                        }
                    }

                    //redirect to success page
                    if (data.success) {
                        window.location = Url.toAbsolute(cdaEntry.fields.successPageUrl);
                    }
                });
            })
            .catch((error) => {
                console.error(error);
                setErrorMsg(cdaEntry.fields.genericErrorMessage);
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    const dataProtectionLabel = (
        <span>
            {insertReactComponentIntoString(
                cdaEntry.fields.labelTermsAndConditionsAgreement,
                '#dataProtectionLink#',
                <Link href="/service/datenschutz/">Datenschutzerklärung</Link>,
            )}
        </span>
    );

    const noSubmitAvailable = formValues.subjectIndex == '6' && formValues.concernIndex == '3';

    return (
        <div className="service-page-contact-form">
            <h2>{cdaEntry.fields.headline}</h2>
            <form onSubmit={handleSubmit} ref={formRef}>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelFirstName}
                        required
                        name="firstName"
                        value={formValues.firstName}
                        onChange={handleChange('firstName')}
                        error={formErrors && !!formErrors.firstName}
                        errorText={formErrors && formErrors.firstName && cdaEntry.fields[formErrors.firstName]}
                    />
                </div>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelLastName}
                        required
                        name="lastName"
                        value={formValues.lastName}
                        onChange={handleChange('lastName')}
                        error={formErrors && !!formErrors.lastName}
                        errorText={formErrors && formErrors.lastName && cdaEntry.fields[formErrors.lastName]}
                    />
                </div>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelCustomerId}
                        name="customerId"
                        value={formValues.customerId}
                        placeholder="optional"
                        type="number"
                        onChange={handleChange('customerId')}
                        error={formErrors && !!formErrors.customerId}
                        errorText={formErrors && formErrors.customerId && cdaEntry.fields[formErrors.customerId]}
                    />
                </div>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelEmail}
                        required
                        inputMode="email"
                        type="email"
                        value={formValues.email}
                        name="email"
                        onChange={handleChange('email')}
                        error={formErrors && !!formErrors.email}
                        errorText={formErrors && formErrors.email && cdaEntry.fields[formErrors.email]}
                    />
                </div>
                <div className="form-row">
                    <TextField
                        label={cdaEntry.fields.labelEmailCompare}
                        required
                        inputMode="email"
                        type="email"
                        value={formValues.emailCompare}
                        name="emailCompare"
                        onChange={handleChange('emailCompare')}
                        error={formErrors && !!formErrors.emailCompare}
                        errorText={formErrors && formErrors.emailCompare && cdaEntry.fields[formErrors.emailCompare]}
                    />
                </div>
                <div className="form-row">
                    <Select
                        label={cdaEntry.fields.labelSubject}
                        required
                        value={formValues.subjectIndex}
                        name="subjectIndex"
                        onValueChange={handleChange('subjectIndex')}
                        error={formErrors && !!formErrors.subjectIndex}
                        errorText={formErrors && formErrors.subjectIndex && cdaEntry.fields[formErrors.subjectIndex]}
                    >
                        {subjectConcerns.map((subjectConcern) => (
                            <Select.Item key={subjectConcern.value} value={subjectConcern.value}>
                                {subjectConcern.subject}
                            </Select.Item>
                        ))}
                    </Select>
                </div>
                <div className="form-row">
                    <Select
                        label={cdaEntry.fields.labelConcern}
                        required
                        value={formValues.concernIndex}
                        name="concernIndex"
                        disabled={formValues.subjectIndex === '0'}
                        onValueChange={handleChange('concernIndex')}
                        error={formErrors && !!formErrors.concernIndex}
                        errorText={formErrors && formErrors.concernIndex && cdaEntry.fields[formErrors.concernIndex]}
                    >
                        {/*<Select.Item value="0">Bitte wählen</Select.Item>*/}
                        {subjectConcerns
                            .find((s) => s.value == formValues.subjectIndex)
                            ?.concerns.map((concern) => (
                                <Select.Item key={`${formValues.subjectIndex}-${concern.value}`} value={concern.value}>
                                    {concern.concern}
                                </Select.Item>
                            ))}
                    </Select>
                    {model.tenantCountry == "de" && <ConcernInfoTextDE />}
                    {model.tenantCountry == "at" && <ConcernInfoTextAT />}
                </div>
                <AdditionalFields formValues={formValues} formErrors={formErrors} cdaEntry={cdaEntry} subjectConcerns={subjectConcerns} />
                {!noSubmitAvailable && (
                    <div className="form-row">
                        <Textarea
                            required
                            name="message"
                            label={cdaEntry.fields.labelMessage}
                            value={formValues.message}
                            onChange={handleChange('message')}
                            error={formErrors && !!formErrors.message}
                            errorText={formErrors && formErrors.message && cdaEntry.fields[formErrors.message]}
                            maxLength="2500"
                        />
                    </div>
                )}
                <div className="form-row">
                    <Checkbox
                        label={dataProtectionLabel}
                        required
                        name="termsAndConditionsAgreement"
                        checked={formValues.termsAndConditionsAgreement}
                        onChange={handleChange('termsAndConditionsAgreement')}
                        value="1"
                        error={formErrors && !!formErrors.termsAndConditionsAgreement}
                        errorText={
                            formErrors &&
                            formErrors.termsAndConditionsAgreement &&
                            cdaEntry.fields[formErrors.termsAndConditionsAgreement]
                        }
                    />
                </div>

                {model.friendlyCaptchaEnabled && (
                    <div className="form-row">
                        <div
                            ref={fcContainer}
                            className="frc-captcha"
                            data-sitekey={friendlyCaptchaSitekey}
                            data-lang="de"
                        ></div>
                        {formErrors && formErrors['frc-captcha-solution'] && (
                            <Alert isOpen severity="error" showCloseIcon={false}>
                                {cdaEntry.fields[formErrors['frc-captcha-solution']]}
                            </Alert>
                        )}
                    </div>
                )}

                {errorMsg && (
                    <div className="form-row">
                        <Alert isOpen severity="error" showCloseIcon={false}>
                            {errorMsg}
                        </Alert>
                    </div>
                )}

                <div className="form-row">
                    <Button type="submit" disabled={noSubmitAvailable || submitButtonIsDisabled} loading={isSubmitting}>
                        {cdaEntry.fields.labelButtonSubmit}
                    </Button>
                </div>
            </form>
        </div>
    );
}
